import { TaskAdmin } from '@gleamer/types';
import React, { useState } from 'react';
import {
  isImportInFlight,
  PredictionImportCommand,
  usePredictionImportCommand,
  usePredictionImportIssues,
  useTaskPredictionImports,
} from '../../../../hooks/useTaskActivity';
import { ButtonTone, PageButton } from '../../../atoms/PageButton';
import { formatDateTime } from '../imports/format';
import { TraceLink } from '../TraceLink';
import { Counts, ImportStatusBadge, Pager } from './ActivityParts';
import {
  allowedImportActions,
  canImportAgain,
  issuePatient,
  modelLabel,
} from './activity';

type PredictionImport = TaskAdmin.PredictionImport;
type PredictionImportAction = TaskAdmin.PredictionImportAction;

/** Issues read per page. */
export const IMPORT_ISSUES_PAGE_SIZE = 50;

const ACTIONS: Record<
  PredictionImportAction,
  { label: string; tone: ButtonTone; confirm?: string }
> = {
  replay: { label: 'Replay', tone: 'go-outline' },
  accept: {
    label: 'Accept',
    tone: 'caution',
    confirm: 'Accept the import as it is? Its dropped lines stay out.',
  },
  abandon: {
    label: 'Abandon',
    tone: 'stop',
    confirm: 'Abandon the import? The task keeps its previous import.',
  },
};

/**
 * One command's button, which says what came of it. A `confirm` text makes the first click ask, inline, before
 * sending.
 */
function CommandButton({
  taskId,
  command,
  label,
  tone,
  confirm,
  disabled,
}: {
  taskId: number;
  command: PredictionImportCommand;
  label: string;
  tone: ButtonTone;
  confirm?: string;
  disabled?: boolean;
}) {
  const mutation = usePredictionImportCommand(taskId);
  const [asking, setAsking] = useState(false);
  const send = () => {
    setAsking(false);
    mutation.mutate(command);
  };
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {asking ? (
        <>
          <span className="text-sm">{confirm}</span>
          <PageButton
            tone={tone === 'stop' ? 'stop' : 'primary'}
            onClick={send}
          >
            Confirm {label.toLowerCase()}
          </PageButton>
          <PageButton onClick={() => setAsking(false)}>Cancel</PageButton>
        </>
      ) : (
        <PageButton
          tone={tone}
          onClick={() => (confirm ? setAsking(true) : send())}
          disabled={disabled || mutation.isLoading}
        >
          {mutation.isLoading ? `${label}…` : label}
        </PageButton>
      )}
      {mutation.isError && (
        <span
          role="alert"
          className="text-sm text-dl-danger"
        >
          {(mutation.error as Error).message}
        </span>
      )}
    </span>
  );
}

/** An import's issues — the lines it dropped, the patients whose rows did not read —, a page at a time; shown when it has some. */
export function ImportIssues({ imported }: { imported: PredictionImport }) {
  const [offset, setOffset] = useState(0);
  const details = usePredictionImportIssues(
    imported.id,
    { limit: IMPORT_ISSUES_PAGE_SIZE, offset },
    isImportInFlight(imported.status)
  );
  if (details.isLoading) {
    return <p className="text-sm text-dl-muted">Loading its issues…</p>;
  }
  if (details.isError) {
    return (
      <p
        role="alert"
        className="text-sm text-dl-danger"
      >
        Could not load its issues: {(details.error as Error)?.message}
      </p>
    );
  }
  const issues = details.data?.issues ?? [];
  const total = details.data?.issueCount ?? issues.length;
  return (
    <div>
      <div className="overflow-x-auto">
        <table
          className="w-full table-auto text-sm"
          aria-label={`Issues of import ${imported.id}`}
        >
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-dl-faint">
              <th className="py-1 pr-2">Patient</th>
              <th className="py-1 pr-2">Line</th>
              <th className="py-1 pr-2">Study / series</th>
              <th className="py-1 pr-2">Reason</th>
              <th className="py-1">At</th>
            </tr>
          </thead>
          <tbody>
            {issues.map((issue, index) => (
              <tr
                key={`${issuePatient(issue)}-${issue.ord}-${index}`}
                className="border-t border-dl-border align-top"
              >
                <td className="break-all py-1 pr-2 font-mono">
                  {issuePatient(issue)}
                </td>
                <td className="whitespace-nowrap py-1 pr-2 tabular-nums">
                  {issue.ord > 0 ? issue.ord : 'all'}
                </td>
                <td className="break-all py-1 pr-2 font-mono text-xs">
                  {issue.studyInstanceUid ?? '—'}
                  {issue.seriesInstanceUid && (
                    <span className="block">{issue.seriesInstanceUid}</span>
                  )}
                </td>
                <td className="py-1 pr-2 font-mono">{issue.reason}</td>
                <td className="whitespace-nowrap py-1 text-dl-muted">
                  {formatDateTime(issue.failedAt)}
                  {issue.replayedAt && (
                    <span className="block text-xs">
                      replaying since {formatDateTime(issue.replayedAt)}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager
        label="issues"
        total={total}
        limit={IMPORT_ISSUES_PAGE_SIZE}
        offset={offset}
        onOffset={setOffset}
      />
    </div>
  );
}

function ImportCard({
  taskId,
  imported,
  latest,
  canSettle,
}: {
  taskId: number;
  imported: PredictionImport;
  latest: boolean;
  canSettle: boolean;
}) {
  const [open, setOpen] = useState(latest);
  const actions = canSettle ? allowedImportActions(imported.status) : [];
  return (
    <li
      aria-label={`Prediction import ${imported.id}`}
      className="rounded-xl border border-dl-border px-4 py-3"
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        className="flex w-full flex-wrap items-center gap-2 text-left text-sm"
      >
        <span aria-hidden>{open ? '▾' : '▸'}</span>
        <ImportStatusBadge status={imported.status} />
        <span className="font-medium text-dl-text">#{imported.id}</span>
        <span className="text-dl-muted">
          {imported.labelTaskRevision != null &&
            `rev. ${imported.labelTaskRevision} · `}
          requested {formatDateTime(imported.requestedAt)}
          {imported.finishedAt &&
            ` · finished ${formatDateTime(imported.finishedAt)}`}
        </span>
      </button>
      {open && (
        <div className="mt-3 flex flex-col gap-3">
          <p className="break-all font-mono text-sm text-dl-text">
            {imported.bqTableId}
          </p>
          <TraceLink
            traceId={imported.traceId}
            logsUrl={imported.logsUrl}
          />
          <Counts
            counts={[
              { label: 'Patients', value: imported.expectedPatients },
              { label: 'Lines read', value: imported.linesRead },
              { label: 'Imported', value: imported.predictionsImported },
              {
                label: 'Dropped',
                value: imported.predictionsDropped,
                tone: 'text-dl-warning',
              },
              {
                label: 'Patients with issues',
                value: imported.failedPatients,
                tone: 'text-dl-danger',
              },
            ]}
          />
          {imported.models.length > 0 && (
            <div className="text-sm">
              <span className="font-medium text-dl-muted">Models: </span>
              {imported.models
                .map(
                  m =>
                    `${modelLabel(m)} (${m.patients.toLocaleString()} patients)`
                )
                .join(', ')}
              {imported.severalModels && (
                <span
                  className="ml-2 rounded-full bg-dl-warning-tint px-2 py-0.5 text-xs font-medium text-dl-warning"
                  title="The table holds several models: the import records the first one it read as its own."
                >
                  Several models
                </span>
              )}
            </div>
          )}
          {imported.failure && (
            <p
              role="alert"
              className="whitespace-pre-wrap break-words rounded-lg bg-dl-danger-tint px-3 py-2 text-sm text-dl-danger"
            >
              {imported.failure}
            </p>
          )}
          {actions.length > 0 && (
            <div className="flex flex-wrap items-center gap-1">
              {actions.map(action => (
                <CommandButton
                  key={action}
                  taskId={taskId}
                  command={{ type: action, importId: imported.id }}
                  label={ACTIONS[action].label}
                  tone={ACTIONS[action].tone}
                  confirm={ACTIONS[action].confirm}
                />
              ))}
            </div>
          )}
          {imported.failedPatients > 0 && <ImportIssues imported={imported} />}
        </div>
      )}
    </li>
  );
}

/**
 * The imports of the task's prediction table, latest first — read again every 5 s while one is requested or
 * importing — with what a data manager (create:prediction) may do with each.
 */
export function PredictionsSection({
  taskId,
  canSettle,
}: {
  taskId: number;
  /** create:labelTask: the section offers to import again, replay, accept and abandon. */
  canSettle: boolean;
}) {
  const imports = useTaskPredictionImports(taskId);
  const list = imports.data ?? [];
  return (
    <section aria-label="Predictions">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h4 className="flex-1 text-lg font-semibold text-dl-text">
          Predictions
        </h4>
        {canSettle && imports.isSuccess && (
          <CommandButton
            taskId={taskId}
            command={{ type: 'import-again' }}
            label="Import again"
            tone="neutral"
            disabled={!canImportAgain(list)}
          />
        )}
      </div>
      {imports.isLoading && (
        <p className="text-sm text-dl-muted">Loading its imports…</p>
      )}
      {imports.isError && (
        <p
          role="alert"
          className="text-sm text-dl-danger"
        >
          Could not load its prediction imports:{' '}
          {(imports.error as Error)?.message}
        </p>
      )}
      {imports.isSuccess && list.length === 0 && (
        <p className="text-sm text-dl-muted">No import of its predictions.</p>
      )}
      {list.length > 0 && (
        <ul className="flex flex-col gap-2">
          {list.map((imported, index) => (
            <ImportCard
              key={imported.id}
              taskId={taskId}
              imported={imported}
              latest={index === 0}
              canSettle={canSettle}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
